import { useState } from "react";
import menuOnClick from "../menuOnClick";

interface NavLink {
  label: string;
  /** 페이지 내 섹션 id (예: "benefits") */
  id: string;
  target?: string;
  className?: string;
}

const navLinks: NavLink[] = [
  { label: "바우하우스 혜택 4가지", id: "benefits" },
  { label: "서비스 확인하기", id: "services" },
  {
    label: "작업사례",
    target: "BLOG",
    id: "blog",
    className: "text-[#4CAF1E]",
  },
  { label: "전국 지사망", id: "branch" },
  { label: "BEST 리뷰 확인하기", id: "best" },
];

/**
 * 같은 페이지 내 섹션이면 부드럽게 스크롤하고, 없으면 실제 URL(/#id)로 이동한다.
 * href를 "/#id" 형태의 실제 경로로 두어 검색엔진(AEO) 크롤러가 링크를 정상 수집하도록 한다.
 */
const handleNavClick = (
  e: React.MouseEvent<HTMLAnchorElement>,
  id: string,
  onDone?: () => void
) => {
  if (document.getElementById(id)) {
    e.preventDefault();
    menuOnClick(`#${id}`);
  }
  onDone?.();
};

const MHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header
      id="header"
      className="fixed top-0 left-0 z-50 w-full border-b border-gray-200 bg-white shadow-sm"
    >
      {/* Top row: logo / tagline / phone */}
      <div className="mx-auto flex items-end justify-between gap-4 px-4 py-3 md:px-6 lg:px-[calc((100%-var(--container))/2)] md:py-4">
        {/* Logo (image asset, per spec) */}
        <a href="/" className="flex shrink-0 items-center">
          <img
            src={"/images/logo.png"}
            alt="바우하우스 BAUHAUS 로고"
            className="h-14 -mb-7 w-auto md:h-full max-h-20 lg:-mb-9"
          />
        </a>

        {/* Tagline — hidden on small screens to save space */}
        <p className="hidden flex-1 text-left md:block md:text-base lg:text-lg lg:pl-5">
          유품정리 · 특수청소 전문 서비스 홈페이지
        </p>

        {/* Phone banner (image asset, per spec) */}
        <a href="tel:010-5629-0176" className="hidden shrink-0 md:block">
          <img
            src={"/images/num.png"}
            alt="무료견적 365일 24시간 출장가능 010.5629.0176"
            className="h-11 w-auto"
          />
        </a>

        {/* Hamburger toggle — mobile only */}
        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label={menuOpen ? "메뉴 닫기" : "메뉴 열기"}
          aria-expanded={menuOpen}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded md:hidden"
        >
          <span className="relative block h-4 w-6">
            <span
              className={`absolute left-0 top-0 block h-0.5 w-6 bg-gray-700 transition-transform ${
                menuOpen ? "translate-y-[7px] rotate-45" : ""
              }`}
            />
            <span
              className={`absolute left-0 top-[7px] block h-0.5 w-6 bg-gray-700 transition-opacity ${
                menuOpen ? "opacity-0" : "opacity-100"
              }`}
            />
            <span
              className={`absolute left-0 top-[14px] block h-0.5 w-6 bg-gray-700 transition-transform ${
                menuOpen ? "-translate-y-[7px] -rotate-45" : ""
              }`}
            />
          </span>
        </button>
      </div>

      {/* Mobile phone banner — shown under logo row on small screens */}
      <div className="flex justify-center border-t border-gray-100 bg-white px-4 py-2 md:hidden">
        <a href="tel:010-5629-0176">
          <img
            src={"/images/num.png"}
            alt="무료견적 365일 24시간 출장가능 010.5629.0176"
            className="h-9 w-auto"
          />
        </a>
      </div>

      {/* Nav row — desktop: always visible bar; mobile: smooth collapsible dropdown */}
      <nav
        className={`overflow-hidden border-t border-gray-200 bg-light transition-[max-height,opacity] duration-300 ease-in-out ${
          menuOpen ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0"
        } md:max-h-none md:overflow-visible md:opacity-100 md:transition-none  text-center`}
      >
        <ul className="mx-auto flex w-full max-w-[1400px] flex-col divide-y divide-gray-200 text-sm md:flex-row md:items-center md:justify-center md:gap-10 lg:gap-14 md:divide-y-0 md:py-3">
          {navLinks.map((link, index) => (
            <li
              key={link.label}
              className={`transition-all duration-300 ease-in-out md:px-0 md:transition-none ${
                menuOpen
                  ? "translate-y-0 opacity-100"
                  : "-translate-y-1 opacity-0"
              } md:translate-y-0 md:opacity-100`}
              style={{ transitionDelay: menuOpen ? `${index * 40}ms` : "0ms" }}
            >
              <a
                href={`/#${link.id}`}
                onClick={(e) =>
                  handleNavClick(e, link.id, () => setMenuOpen(false))
                }
                className={`block py-3 font-medium text-lg hover:text-primary md:py-0`}
              >
                {link.target && (
                  <span className={`${link.className ?? ""}`}>
                    {link.target}&nbsp;
                  </span>
                )}
                {link.label}
                {link.label === "전국 지사망" && (
                  <span className="text-[#F4791C]"> (지사 모집중)</span>
                )}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </header>
  );
};

export default MHeader;
